import {
  TAO6TaskType,
  TAO6DatasetFormat,
  TAO6Dataset,
  TAO6Experiment,
  TAO6PretrainedModel,
  TAO6InstanceDetails,
  TAOInstanceDetails,
} from './types';
import { TAO6_PRETRAINED_MODELS } from './pretrained-models';
import { TAO6_SAMPLE_DATASETS } from './datasets';

export const mockTAO6Experiments = (): TAO6Experiment[] => [
  {
    id: 'exp-yolov8-custom-001',
    name: 'yolov8_custom_detection',
    task: 'object_detection',
    pretrainedModel: TAO6_PRETRAINED_MODELS.find(m => m.id === 'yolov8_m')!,
    dataset: TAO6_SAMPLE_DATASETS.find(d => d.id === 'coco-custom-detection')!,
    status: 'training',
    progress: 67,
    config: {
      epochs: 100,
      batchSize: 16,
      learningRate: 0.001,
      optimizer: 'adamw',
      augmentations: ['mosaic', 'mixup', 'random_flip'],
      enableQAT: false,
      enableKD: false,
      multiGPU: false,
      gpuCount: 1,
    },
    metrics: {
      currentEpoch: 67,
      totalEpochs: 100,
      trainLoss: 0.0234,
      valLoss: 0.0312,
      mAP: 0.847,
      mAP50: 0.912,
      precision: 0.89,
      recall: 0.82,
    },
    training: {
      startedAt: Date.now() - 3600000 * 4,
      estimatedCompletion: Date.now() + 3600000 * 2,
      gpuUtilization: 94,
      gpuMemoryUsed: 8.5,
    },
    exports: [],
    createdAt: Date.now() - 86400000,
    updatedAt: Date.now() - 300000,
  },
  {
    id: 'exp-resnet50-product-002',
    name: 'resnet50_product_classification',
    task: 'image_classification',
    pretrainedModel: TAO6_PRETRAINED_MODELS.find(m => m.id === 'resnet_50')!,
    dataset: TAO6_SAMPLE_DATASETS.find(d => d.id === 'product-classification')!,
    status: 'completed',
    progress: 100,
    config: {
      epochs: 50,
      batchSize: 32,
      learningRate: 0.0001,
      optimizer: 'adam',
      augmentations: ['random_crop', 'random_flip', 'color_jitter'],
      enableQAT: true,
      enableKD: false,
      multiGPU: false,
      gpuCount: 1,
    },
    metrics: {
      currentEpoch: 50,
      totalEpochs: 50,
      trainLoss: 0.0089,
      valLoss: 0.0134,
      accuracy: 0.965,
      precision: 0.96,
      recall: 0.95,
      f1Score: 0.955,
    },
    training: {
      startedAt: Date.now() - 86400000 * 2,
      gpuUtilization: 0,
      gpuMemoryUsed: 0,
    },
    exports: [
      {
        id: 'export-001',
        format: 'onnx',
        status: 'completed',
        path: '/workspace/exports/resnet50_product.onnx',
        size: '98 MB',
        createdAt: Date.now() - 86400000,
      },
      {
        id: 'export-002',
        format: 'tensorrt',
        status: 'completed',
        path: '/workspace/exports/resnet50_product.engine',
        size: '45 MB',
        createdAt: Date.now() - 86400000,
      },
    ],
    createdAt: Date.now() - 86400000 * 3,
    updatedAt: Date.now() - 86400000,
  },
  {
    id: 'exp-dino-retail-003',
    name: 'dino_retail_detection',
    task: 'object_detection',
    pretrainedModel: TAO6_PRETRAINED_MODELS.find(m => m.id === 'dino_resnet50')!,
    dataset: TAO6_SAMPLE_DATASETS.find(d => d.id === 'retail-detection')!,
    status: 'queued',
    progress: 0,
    config: {
      epochs: 80,
      batchSize: 8,
      learningRate: 0.0001,
      optimizer: 'adamw',
      augmentations: ['random_flip', 'random_crop'],
      enableQAT: false,
      enableKD: true,
      multiGPU: false,
      gpuCount: 1,
    },
    metrics: {
      currentEpoch: 0,
      totalEpochs: 80,
      trainLoss: 0,
    },
    training: {
      startedAt: 0,
      gpuUtilization: 0,
      gpuMemoryUsed: 0,
    },
    exports: [],
    createdAt: Date.now() - 3600000,
    updatedAt: Date.now() - 3600000,
  },
  {
    id: 'exp-swin-imagenet-004',
    name: 'swin_imagenet_classification',
    task: 'image_classification',
    pretrainedModel: TAO6_PRETRAINED_MODELS.find(m => m.id === 'swin_tiny')!,
    dataset: TAO6_SAMPLE_DATASETS.find(d => d.id === 'imagenet-subset')!,
    status: 'evaluating',
    progress: 100,
    config: {
      epochs: 30,
      batchSize: 16,
      learningRate: 0.00005,
      optimizer: 'adamw',
      augmentations: ['random_crop', 'random_flip', 'mixup', 'cutmix'],
      enableQAT: false,
      enableKD: false,
      multiGPU: true,
      gpuCount: 2,
    },
    metrics: {
      currentEpoch: 30,
      totalEpochs: 30,
      trainLoss: 0.0156,
      valLoss: 0.0198,
      accuracy: 0.938,
      precision: 0.94,
      recall: 0.93,
      f1Score: 0.935,
    },
    training: {
      startedAt: Date.now() - 86400000,
      gpuUtilization: 45,
      gpuMemoryUsed: 4.2,
    },
    exports: [],
    createdAt: Date.now() - 86400000 * 2,
    updatedAt: Date.now() - 1800000,
  },
];

// Helper function to get model card info with defaults for models missing extended fields
export const getModelCardInfo = (model: TAO6PretrainedModel): TAO6PretrainedModel => {
  // If model already has all fields, return as is
  if (model.pretrainedDataset && model.useCases) {
    return model;
  }

  // Provide defaults based on task type
  const getDefaultsForTask = (task: TAO6TaskType) => {
    switch (task) {
      case 'object_detection':
        return {
          dataset: 'COCO 2017',
          classes: 80,
          useCases: ['Object detection', 'Visual inspection', 'Surveillance', 'Autonomous systems'],
        };
      case 'image_classification':
        return {
          dataset: 'ImageNet-1K',
          classes: 1000,
          useCases: ['Image classification', 'Feature extraction', 'Transfer learning', 'Visual recognition'],
        };
      case 'instance_segmentation':
        return {
          dataset: 'COCO 2017',
          classes: 80,
          useCases: ['Instance segmentation', 'Object counting', 'Scene understanding', 'Medical imaging'],
        };
      case 'semantic_segmentation':
        return {
          dataset: 'ADE20K',
          classes: 150,
          useCases: ['Scene parsing', 'Autonomous driving', 'Medical imaging', 'Satellite imagery'],
        };
      case 'pose_estimation':
        return {
          dataset: 'COCO Keypoints',
          classes: 17,
          useCases: ['Human pose estimation', 'Sports analysis', 'Fitness tracking', 'Motion capture'],
        };
      case 'action_recognition':
        return {
          dataset: 'Kinetics-400',
          classes: 400,
          useCases: ['Video classification', 'Activity detection', 'Surveillance', 'Sports analysis'],
        };
      case 'ocr':
        return {
          dataset: 'MJSynth + SynthText',
          classes: 96,
          useCases: ['Text recognition', 'Document digitization', 'License plate reading', 'Scene text'],
        };
      default:
        return {
          dataset: 'Custom',
          classes: 100,
          useCases: ['General purpose', 'Transfer learning'],
        };
    }
  };

  const defaults = getDefaultsForTask(model.task);

  return {
    ...model,
    pretrainedDataset: model.pretrainedDataset || defaults.dataset,
    pretrainedClasses: model.pretrainedClasses || defaults.classes,
    useCases: model.useCases || defaults.useCases,
    strengths: model.strengths || [
      model.vramRequired <= 4 ? 'Low memory footprint' : 'High capacity',
      model.accuracy > 80 ? 'High accuracy' : 'Balanced performance',
      model.supportsQAT ? 'QAT support for deployment' : 'Good for fine-tuning',
    ],
    limitations: model.limitations || [
      model.vramRequired > 16 ? 'High GPU memory requirement' : 'May need more data',
      'Requires domain-specific fine-tuning',
    ],
    license: model.license || 'Apache 2.0',
  };
};

// Mock TAO 6.x Instance Details function
export const mockTAO6InstanceDetails = (taskId: string): TAO6InstanceDetails => ({
  taoVersion: '6.0.0',
  status: 'active',
  gpuUtilization: 78,
  gpuMemoryUsed: 32,
  gpuMemoryTotal: 40,
  experiments: mockTAO6Experiments(),
  datasets: TAO6_SAMPLE_DATASETS,
  pretrainedModels: TAO6_PRETRAINED_MODELS,
  workspacePaths: {
    root: '/workspace',
    datasets: '/workspace/datasets',
    experiments: '/workspace/experiments',
    exports: '/workspace/exports',
    logs: '/workspace/logs',
  },
  notebookUrl: `http://localhost:8888/lab?token=tao6-${taskId.slice(0, 8)}`,
});

// Legacy mock function for backward compatibility
export const mockTAOInstanceDetails = (taskId: string): TAOInstanceDetails => ({
  taoVersion: '5.2.0',
  status: 'active',
  experiments: [
    {
      name: 'yolov8_custom_detection',
      task: 'detection',
      baseModel: 'YOLOv8-M',
      status: 'training',
      progress: 67,
      metrics: {
        epoch: 67,
        totalEpochs: 100,
        loss: 0.0234,
        mAP: 0.847,
      },
    },
    {
      name: 'resnet50_product_classification',
      task: 'classification',
      baseModel: 'ResNet-50',
      status: 'completed',
      progress: 100,
      metrics: {
        epoch: 50,
        totalEpochs: 50,
        loss: 0.0089,
        accuracy: 0.965,
      },
    },
  ],
  datasetPath: '/workspace/datasets/custom_coco',
  checkpointsPath: '/workspace/checkpoints',
  notebookUrl: `http://localhost:8888/lab?token=tao-${taskId.slice(0, 8)}`,
});

// Helper functions for TAO 6.x
export const getTAO6ModelsByTask = (task: TAO6TaskType): TAO6PretrainedModel[] => {
  return TAO6_PRETRAINED_MODELS.filter(model => model.task === task);
};

export const getTAO6DatasetsByTask = (task: TAO6TaskType): TAO6Dataset[] => {
  return TAO6_SAMPLE_DATASETS.filter(dataset => dataset.task === task);
};

export const getTAO6DatasetsByFormat = (format: TAO6DatasetFormat): TAO6Dataset[] => {
  return TAO6_SAMPLE_DATASETS.filter(dataset => dataset.format === format);
};
